/** Changing the URL without leaving the route you are already on. */
import { useCallback } from "react";
import { useMatches, useNavigate } from "@tanstack/react-router";

/** URL values are always strings, exactly as Next's `router.query` was. */
export type UrlRecord = Record<string, string>;

/** Given the values the URL is about to be built from, returns the new ones. */
type UrlUpdater = (previous: UrlRecord) => UrlRecord;

export type SameRouteUpdate = {
  /** Rewrites the query string. */
  search: UrlUpdater;
  /** Rewrites the path segments — only for a key that is one. */
  params?: UrlUpdater;
  replace?: boolean;
};

/**
 * The route pattern the current URL matched — `/$planId/proxy/list`, not
 * `/36/proxy/list`. This is what Next's `router.pathname` held, down to the
 * bracket-versus-dollar spelling, and pushing to the *pattern* rather than the
 * concrete path is what let a navigation change a path segment.
 *
 * Read off the last match rather than the nearest one: every match carries the
 * full params, but each carries only its own `fullPath`, so a component
 * rendered by a layout route would otherwise aim at the layout's path.
 */
function useRoutePattern(): string {
  return useMatches({
    select: (matches) => matches[matches.length - 1]?.fullPath ?? "/",
  });
}

/**
 * `router.push({ pathname: router.pathname, query })`, natively.
 *
 * Two things this deliberately is not:
 *
 * - It is not `to: "."`, and not `to` omitted either — both resolve relatively,
 *   which appends a trailing slash to any index route (`/36/proxy/list` becomes
 *   `/36/proxy/list/`). Next rebuilt the URL from the route pattern, which has
 *   no trailing slash, and `trailingSlash: "preserve"` means nothing normalises
 *   one away afterwards (ANO-117). The pattern is passed explicitly instead,
 *   with TanStack's index-route slash trimmed off it.
 *
 * - It is not a built `href`. An href carries a finished query string, so two
 *   hooks writing in the same tick would each overwrite the other's key with
 *   their own render-time snapshot. Updaters compose: the router builds each
 *   navigation from the previous one's pending location, so the second write
 *   sees the first. Next could not do this, which is why the code that used to
 *   live here mutated `router.query` in place before pushing.
 */
export function useSameRouteNavigate() {
  const navigate = useNavigate();
  const routePattern = useRoutePattern();

  return useCallback(
    ({ params, replace, search }: SameRouteUpdate) => {
      // TanStack spells an index route's `fullPath` as `/$planId/proxy/list/`
      // and its `to` as `/$planId/proxy/list`. Trimming yields a member of the
      // `to` union, but as a plain `string` the type system cannot see that —
      // and `to` is what makes `params`/`search` concrete, so the options go in
      // as one cast. It is the single cast this approach costs, kept here
      // rather than at every call site.
      const to =
        routePattern.length > 1 && routePattern.endsWith("/")
          ? routePattern.slice(0, -1)
          : routePattern;

      // `shallow` is not represented as an option: no route here has a loader,
      // so every navigation was already shallow under Next. Its *scroll* half
      // is, and has to be: Next skipped the scroll reset only for a shallow
      // push that stayed on the same route (`shouldScroll = scroll ??
      // (!isQueryUpdating && !isValidShallowRoute)`, in
      // next/dist/shared/lib/router/router.js), and this hook is by definition
      // that case. TanStack's `resetScroll` defaults to true everywhere, which
      // put the user back at the top on every filter, tab or setting change.
      // Genuine route changes go through `useNavigate` and keep the
      // scroll-to-top default, as they had under Next.
      //
      // `hash` is the same argument as `resetScroll`, about the same intent. A
      // filter, tab or toggle is not a navigation away from the anchor the
      // reader is standing on, so the hash carries forward and the viewport
      // stays put. TanStack drops the hash when `hash` is omitted (a location
      // is built with `hash: undefined`) and, because `scrollRestoration` is
      // on, scrolls to it on every navigation that keeps it — which would yank
      // a reader back to the anchor on every toggle. `hash: true` without
      // `hashScrollIntoView: false` is therefore worse than dropping it;
      // scrolling to an anchor is `useAnchorScroll`'s job, and it yields to
      // the reader. A deliberate divergence from Next, which dropped the hash
      // from `router.push({ pathname, query })` as well (R-2036).
      void navigate({
        hash: true,
        hashScrollIntoView: false,
        params,
        replace,
        resetScroll: false,
        search,
        to,
      } as Parameters<typeof navigate>[0]);
    },
    [navigate, routePattern]
  );
}
